import type { PaletteOptions } from "@mui/material/styles";

/** Default brand colours. Overridden per-tenant at runtime by `/api/branding`. */
export const BRAND = {
  primary: "#4f46e5", // indigo
  secondary: "#7c3aed", // violet
} as const;

/** Semantic status colours — the single source of truth for `StatusChip`. */
export const STATUS_COLORS = {
  neutral: { light: "#64748b", dark: "#8b949e" },
  info: { light: "#4f46e5", dark: "#818cf8" },
  success: { light: "#059669", dark: "#34d399" },
  warning: { light: "#b45309", dark: "#fbbf24" },
  error: { light: "#dc2626", dark: "#f87171" },
  running: { light: "#4f46e5", dark: "#818cf8" },
} as const;

const GREY = {
  50: "#f8fafc",
  100: "#f1f5f9",
  200: "#e8eef5",
  300: "#cbd5e1",
  400: "#94a3b8",
  500: "#64748b",
  600: "#475569",
  700: "#334155",
  800: "#1e293b",
  900: "#0f172a",
};

export function lightPalette(primary: string, secondary: string): PaletteOptions {
  return {
    mode: "light",
    primary: { main: primary },
    secondary: { main: secondary },
    success: { main: "#059669" },
    warning: { main: "#b45309" },
    error: { main: "#dc2626" },
    info: { main: primary },
    grey: GREY,
    // secondary is GREY[600] (not [500]) so 13px body2 clears WCAG AA on the
    // off-white page background.
    text: { primary: GREY[900], secondary: GREY[600], disabled: GREY[400] },
    divider: GREY[200],
    background: { default: "#f6f7fb", paper: "#ffffff" },
  };
}

export function darkPalette(primary: string, secondary: string): PaletteOptions {
  return {
    mode: "dark",
    primary: { main: shiftForDark(primary) },
    secondary: { main: shiftForDark(secondary) },
    success: { main: "#34d399" },
    warning: { main: "#fbbf24" },
    error: { main: "#f87171" },
    info: { main: shiftForDark(primary) },
    grey: GREY,
    text: { primary: "#e6edf3", secondary: "#8b949e", disabled: "#484f58" },
    divider: "#21262d",
    background: { default: "#0d1117", paper: "#161b22" },
  };
}

/** Lighten a hex colour ~25% so brand colours stay legible on dark surfaces. */
function shiftForDark(hex: string): string {
  const m = /^#?([\da-f]{6})$/i.exec(hex.trim());
  if (!m?.[1]) return hex;
  const n = Number.parseInt(m[1], 16);
  const mix = (c: number) => Math.round(c + (255 - c) * 0.25);
  const r = mix((n >> 16) & 0xff);
  const g = mix((n >> 8) & 0xff);
  const b = mix(n & 0xff);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}
